import { Component, effect, input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    RRuleEnd,
    rruleEndEnumOptions,
    RRuleFrequency,
    rruleFrequencyEnumOptions,
    rruleMonthDayPositionEnumOptions,
    rruleMonthNameEnumOptions,
    RRuleWeekDay,
    rruleWeekDayEnumOptions,
} from './rrule-input.types';
import moment from 'moment';
import { TranslatePipe } from '@ngx-translate/core';
import { fb } from 'packages/client/src/app/modules/shared/shared.global-service';
import { startWith, Subscription } from 'rxjs';
import { RRule, rrulestr } from 'rrule';
import { SelectInputComponent } from '../select-input/select-input.component';
import { NumberInputComponent } from '../text-input/number-input.component';
import { RadioInputComponent } from '../radio-input/radio-input.component';
import { DateInputComponent } from '../date-input/date-input.component';
import { ByWeekday } from 'rrule/dist/esm/types';
import { MultiSelectInputComponent } from '../multi-select-input/multi-select-input.component';
import { Chip } from '@openng/optimus-ui/chip';
import { AppFormGroup } from '../../../../utils/form.utils';
import { FormatRRulePipe } from '../../../../modules/shared/pipes/format-rrule.pipe';

@Component({
    selector: 'app-rrule-input',
    templateUrl: './r-rule-input.component.html',
    styleUrl: './r-rule-input.component.scss',
    imports: [
        TranslatePipe,
        SelectInputComponent,
        NumberInputComponent,
        RadioInputComponent,
        DateInputComponent,
        MultiSelectInputComponent,
        Chip,
        FormatRRulePipe,
    ],
})
/**
 * @Deprecated("use signal based v2")
 */
export class RRuleInputComponent implements OnInit {

    public id = input<string>('recurrency');
    public form = input.required<FormGroup>();
    public disabled = input<boolean>(false);

    public showStartDate = input<boolean>(true);
    public labelStartDate = input<string>('inputs.rrule.startDate');

    protected innerForm = new AppFormGroup({
        dtstart: fb.control<Date | null>(null),

        freq: fb.control<RRuleFrequency | null>(null),
        interval: fb.control<number | null>(null),

        byweekday: fb.control<ByWeekday[] | null>(null),

        type: fb.control<string | null>(null),

        bysetpos: fb.control<number[] | null>(null),
        bymonthday: fb.control<number[] | null>(null),
        bymonth: fb.control<number[] | null>(null),

        endtype: fb.control<RRuleEnd | null>(null),
        count: fb.control<number | null>(null),
        until: fb.control<Date | null>(null),
    });

    protected prefix = '';
    protected ruleSub?: Subscription;

    protected frequencyChanged = this.innerForm.signalCodeChange<RRuleFrequency | null>('freq');
    protected typeChanged = this.innerForm.signalCodeChange<string | null>('type');
    protected endTypeChanged = this.innerForm.signalCodeChange<RRuleEnd | null>('endtype');

    protected innerFormValueChanged = this.innerForm.signalCodeChange();

    constructor() {
        effect(() => {
            const freq = this.frequencyChanged();

            if (freq.isCodeUpdate) return;

            if (freq.value === null || freq.value === undefined) {
                this.innerForm.patchValue({
                    interval: null,
                    byweekday: null,
                    bysetpos: null,
                    bymonth: null,
                    bymonthday: null,
                    count: null,
                    until: null,

                    type: null,
                    endtype: null,
                });
            } else {
                const nowDate = moment();
                this.innerForm.patchValue({
                    interval: this.innerForm.value.interval || 1,
                    byweekday: freq.value === RRuleFrequency.WEEKLY ? [Number(nowDate.format('d')) ? Number(nowDate.format('d')) - 1 : 6] : [],
                    bysetpos: [],
                    bymonth: freq.value === RRuleFrequency.YEARLY ? [nowDate.month() + 1] : [],
                    bymonthday: freq.value === RRuleFrequency.MONTHLY || freq.value === RRuleFrequency.YEARLY ? [nowDate.date()] : [],

                    type: freq.value === RRuleFrequency.MONTHLY || freq.value === RRuleFrequency.YEARLY ? 'bymonthday' : null,
                });
            }
        });

        effect(() => {
            const type = this.typeChanged();

            if (type.isCodeUpdate) return;

            const nowDate = moment();

            if (type.value === 'bymonthday') {
                this.innerForm.patchValue({
                    bymonthday: [nowDate.date()],
                    bysetpos: [],
                    byweekday: [],
                });
            } else if (type.value === 'bysetpos') {
                this.innerForm.patchValue({
                    bymonthday: null,
                    bysetpos: [this.getWeekdayOccurrenceInMonth(nowDate)],
                    byweekday: [Number(nowDate.format('d')) ? Number(nowDate.format('d')) - 1 : 6],
                });
            }
        });

        effect(() => {
            const endType = this.endTypeChanged();

            if (endType.isCodeUpdate) return;

            if (endType.value === RRuleEnd.COUNT && !this.innerForm.value.count) {
                this.innerForm.controls.count.setValue(10);
                this.innerForm.controls.until.setValue(null);
            }
            if (endType.value === RRuleEnd.UNTIL && !this.innerForm.value.until) {
                this.innerForm.controls.until.setValue(moment().add(2, 'month').toDate());
                this.innerForm.controls.count.setValue(null);
            }
            if (endType.value === RRuleEnd.NEVER) {
                this.innerForm.controls.until.setValue(null);
                this.innerForm.controls.count.setValue(null);
            }
        });

        effect(() => {
            this.ruleSub?.unsubscribe();
            this.ruleSub = this.form().controls[this.id()].valueChanges.pipe(startWith(this.form().controls[this.id()].value)).subscribe((value: string | null) => {
                value && this.innerForm.patchValue(this.createFormObject(value), { isCodeUpdate: true });
            });
        });

        effect(() => {
            const val = this.innerFormValueChanged();
            if (val.isCodeUpdate) return;

            this.form().controls[this.id()].setValue(this.createRRuleFromOpts(val.value)?.toString(), { emitEvent: false });
        });
    }

    ngOnInit(): void {
        setTimeout(() => {
            if (this.disabled()) {
                this.innerForm.disable({ emitEvent: false });
            } else {
                this.innerForm.enable({ emitEvent: false });
            }
        });
    }

    public setInputData(data: any) {
        const newData = {
            ...this.innerForm.value,
            ...data,
        };

        this.innerForm.patchValue({
            ...newData,
            freq: newData.freq as unknown as RRuleFrequency,
            type: newData.bysetpos?.length ? 'bysetpos' : newData.bymonthday?.length ? 'bymonthday' : null,
            endtype: newData.count ? RRuleEnd.COUNT : newData.until ? RRuleEnd.UNTIL : RRuleEnd.NEVER,
        });
    }

    protected monthlyTypeOptions = [
        { name: '', value: 'bymonthday' },
        { name: '', value: 'bysetpos' },
    ];

    private createFormObject(rrule: string) {
        const opts = rrulestr(rrule).options;

        return {
            ...opts,
            freq: opts.freq as unknown as RRuleFrequency,
            type: opts.bysetpos?.length ? 'bysetpos' : opts.bymonthday?.length ? 'bymonthday' : null,
            endtype: opts.count ? RRuleEnd.COUNT : opts.until ? RRuleEnd.UNTIL : RRuleEnd.NEVER,
        };
    }

    private getWeekdayOccurrenceInMonth(date = moment()) {
        const currentDay = date.date();
        const weekday = date.day();

        let count = 0;
        for (let day = 1; day <= currentDay; day++) {
            if (moment(date).date(day).day() === weekday) {
                count++;
            }
        }
        return count <= 4 ? 4 : -1;
    }

    private createRRuleFromOpts(opts?: any): RRule | null {
        if (!opts || opts.freq == null) return null;

        const newOpts = { ...opts };
        delete newOpts.type;
        delete newOpts.endtype;

        return new RRule(newOpts);
    }

    protected readonly RRuleFrequency = RRuleFrequency;
    protected readonly rruleFrequencyEnumOptions = rruleFrequencyEnumOptions;

    protected readonly RRuleWeekDay = RRuleWeekDay;
    protected readonly rruleWeekDayEnumOptions = rruleWeekDayEnumOptions;

    protected readonly RRuleEnd = RRuleEnd;
    protected readonly rruleEndEnumOptions = rruleEndEnumOptions;

    protected readonly rruleMonthDayPositionEnumOptions = rruleMonthDayPositionEnumOptions;
    protected readonly rruleMonthNameEnumOptions = rruleMonthNameEnumOptions;
}
